import Link from 'next/link'
import React, { useMemo } from 'react'

import { PixieWorldLogo } from '@/components/Logo/PixieWorldLogo'
import { usePixieWorldAuthContext } from '@/shared/auth/pixieWorldAuthContext'
import { pxToRem } from '@/shared/styles/themes'
import { AppBar, Container, css, Grid2 as Grid, Slide, styled, useScrollTrigger } from '@mui/material'

import { DesktopSearchBar } from '../SearchBar/DesktopSearchBar'
import { CartIcon } from './CartIcon'
import { NavItem } from './NavItem'
import { usePrimaryNavigationContext } from './PrimaryNavigationContext'
import { Profile } from './Profile'
import { StyledDesktopSearchContainer, StyledLogoWrapper, StyledRightMenuContainer } from './StyledNavContainers'

export type PrimaryNavigationProps = {
  onAuthModalOpen?: () => void
  authenticated: boolean
  transparent?: boolean
  categoryChildren?: React.ReactNode
  staticHeader?: boolean
  scrollWithPage?: boolean
}

export const PrimaryNavigation: React.FC<PrimaryNavigationProps> = ({
  onAuthModalOpen,
  authenticated,
  transparent,
  staticHeader,
  scrollWithPage,
}) => {
  const { profile } = usePixieWorldAuthContext()

  // Navigation items for both desktop and mobile
  // Show "Library" for authenticated + onboarded users, "Start" otherwise
  const navigationItems = useMemo(() => {
    if (authenticated && profile?.is_onboarded) {
      return [{ label: 'Library', href: '/library' }]
    }

    return [{ label: 'Explore', href: '/shop' }]
  }, [authenticated, profile])

  const appBarContent = (
    <StyledNavigationContainer maxWidth="lg">
      <Grid container justifyContent="space-between" alignItems="center" wrap="nowrap" sx={{ height: '100%' }}>
        <StyledLogoWrapper item>
          <Link rel="preload" href="/" passHref style={{ textDecoration: 'none' }}>
            <PixieWorldLogo colorVariant="color" />
          </Link>
        </StyledLogoWrapper>
        <StyledDesktopSearchContainer item>
          <DesktopSearchBar />
        </StyledDesktopSearchContainer>
        <StyledRightMenuContainer item>
          <StyledNavItemsContainer
            container
            justifyContent="space-between"
            columnSpacing={{ xs: 1, sm: 2, md: 3 }}
            alignItems="center"
            wrap="nowrap"
          >
            {/* Desktop navigation links - hidden on mobile */}
            <StyledDesktopNavLinks>
              {navigationItems.map((item, index) => (
                <NavItem key={index} href={item.href}>
                  {item.label}
                </NavItem>
              ))}
            </StyledDesktopNavLinks>

            <Grid sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: pxToRem(14) }}>
              <CartIcon />
              <Profile
                authenticated={authenticated}
                onNonLoggedInClick={onAuthModalOpen}
                profileUrl={profile?.profile_picture}
              />
            </Grid>

          </StyledNavItemsContainer>
        </StyledRightMenuContainer>
      </Grid>
    </StyledNavigationContainer>
  )

  return (
    <>
      {!transparent && !staticHeader && !scrollWithPage && <StyledBox />}
      {scrollWithPage ? (
        <StyledNavigationAppBar
          position="static"
          $transparent={transparent}
        >
          {appBarContent}
        </StyledNavigationAppBar>
      ) : staticHeader ? (
        <StyledNavigationAppBar
          position="sticky"
          $transparent={transparent}
        >
          {appBarContent}
        </StyledNavigationAppBar>
      ) : (
        <HideOnScroll>
          <StyledNavigationAppBar
            $transparent={transparent}
          >
            {appBarContent}
          </StyledNavigationAppBar>
        </HideOnScroll>
      )}

    </>
  )
}

const StyledDesktopNavLinks = styled('div')`
  display: none;
  
  ${props => props.theme.breakpoints.up('md')} {
    display: flex;
    gap: ${pxToRem(16)};
    align-items: center;
  }
`

const HideOnScroll = ({ children }: {
  children: React.ReactElement
}) => {
  const trigger = useScrollTrigger({ threshold: 64 })
  const { isSearchOpen } = usePrimaryNavigationContext()

  return (
    <Slide appear={false} direction="down" in={!trigger || isSearchOpen}>
      {children}
    </Slide>
  )
}

const StyledBox = styled('div')`
  margin-top: ${pxToRem(64)};

  ${props => props.theme.breakpoints.up('md')} {
    margin-top: ${pxToRem(80)};
  }
`

const StyledNavItemsContainer = styled(Grid)`
  margin-right: ${pxToRem(-12)};
`

const StyledNavigationAppBar = styled(AppBar, {
  shouldForwardProp: (prop) => !['$transparent'].includes(prop as string),
}) <{ $transparent?: boolean }>`
  background-color: ${props => props.$transparent ? 'transparent' : props.theme.palette.primary.main};
  color: #F5F2EE;
  height: ${pxToRem(65)};
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-right: 0;
  box-shadow: none;

  ${props => props.theme.breakpoints.up('md')} {
    height: ${pxToRem(81)};
  }

  ${props => props.$transparent && css`
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      background-color: rgba(19, 40, 72, 0.75);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      z-index: 0;
    }

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background-image:
        radial-gradient(1px 1px at 10% 20%, rgba(255, 255, 255, 0.4) 50%, transparent 100%),
        radial-gradient(1px 1px at 25% 60%, rgba(255, 255, 255, 0.3) 50%, transparent 100%),
        radial-gradient(1.5px 1.5px at 40% 15%, rgba(255, 255, 255, 0.35) 50%, transparent 100%),
        radial-gradient(1px 1px at 55% 45%, rgba(255, 255, 255, 0.3) 50%, transparent 100%),
        radial-gradient(1px 1px at 70% 75%, rgba(255, 255, 255, 0.4) 50%, transparent 100%),
        radial-gradient(1.5px 1.5px at 85% 30%, rgba(255, 255, 255, 0.3) 50%, transparent 100%),
        radial-gradient(1px 1px at 15% 80%, rgba(255, 255, 255, 0.35) 50%, transparent 100%),
        radial-gradient(1px 1px at 60% 10%, rgba(255, 255, 255, 0.3) 50%, transparent 100%),
        radial-gradient(1.5px 1.5px at 90% 65%, rgba(255, 255, 255, 0.4) 50%, transparent 100%),
        radial-gradient(1px 1px at 35% 90%, rgba(255, 255, 255, 0.3) 50%, transparent 100%),
        radial-gradient(1px 1px at 75% 50%, rgba(255, 255, 255, 0.35) 50%, transparent 100%),
        radial-gradient(1.5px 1.5px at 5% 45%, rgba(255, 255, 255, 0.3) 50%, transparent 100%),
        radial-gradient(1px 1px at 50% 85%, rgba(255, 255, 255, 0.4) 50%, transparent 100%),
        radial-gradient(1px 1px at 95% 15%, rgba(255, 255, 255, 0.3) 50%, transparent 100%);
      pointer-events: none;
      z-index: 0;
    }

    & > * {
      position: relative;
      z-index: 1;
    }
  `}

  &.MuiPaper-root {
    border-radius: 0;
  }
`

const StyledNavigationContainer = styled(Container)`
  height: 100%;
`